/**
 * useAuth.ts
 * ──────────
 * Single auth hook consumed throughout the app.
 *
 * isAuthenticated / isLoading / user → SESSION STORE (not Auth0 directly).
 * loginWithRedirect / logout          → proxied through Auth0 SDK.
 *
 * Auth0 is never the session source of truth — only the identity provider
 * used at login/logout time.
 */

import { useEffect, useReducer } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
  getSession,
  onSessionChange,
  setExplicitLogoutHandoff,
} from "@/lib/sessionStore";
import { clearServerSession, getLoginRedirect } from "@/contexts/AuthClientContext";

const PUSH_LOGOUT_CLEANUP_TIMEOUT_MS = 3000;

async function cleanupCurrentDevicePushBeforeLogout(): Promise<void> {
  let timeoutId: number | undefined;
  const cleanup = import("@/lib/push-notifications")
    .then(({ pushNotificationManager }) => pushNotificationManager.unsubscribe())
    .then(
      (success) => ({ status: "finished" as const, success }),
      () => ({ status: "failed" as const }),
    );

  const result = await Promise.race([
    cleanup,
    new Promise<{ status: "timeout" }>((resolve) => {
      timeoutId = window.setTimeout(
        () => resolve({ status: "timeout" }),
        PUSH_LOGOUT_CLEANUP_TIMEOUT_MS,
      );
    }),
  ]);

  if (timeoutId !== undefined) window.clearTimeout(timeoutId);
  if (result.status === "timeout") {
    console.warn("[Auth] Push cleanup timed out; continuing logout");
  } else if (result.status === "failed" || !result.success) {
    console.warn("[Auth] Push cleanup did not complete; continuing logout");
  }
}

export function useAuth() {
  // Re-render whenever session store changes
  const [, forceUpdate] = useReducer((x: number) => x + 1, 0);
  useEffect(() => onSessionChange(forceUpdate), []);

  const session = getSession();
  const { logout: auth0Logout } = useAuth0();

  const isAuthenticated = session.initialized && session.mode !== "none";
  const isLoading = !session.initialized;

  const loginWithRedirect = async (opts?: any) => {
    const fn = getLoginRedirect();
    await fn(opts);
  };

  const logout = async (opts?: any) => {
    await cleanupCurrentDevicePushBeforeLogout();
    setExplicitLogoutHandoff();
    clearServerSession();
    if (opts?.clearStorage === true) {
      localStorage.clear();
      sessionStorage.clear();
    } else {
      localStorage.removeItem("showme_device_token");
    }
    // clearStorage can remove the handoff marker; restore it before redirecting.
    setExplicitLogoutHandoff();
    auth0Logout({
      logoutParams: {
        ...(opts?.logoutParams || {}),
        returnTo: `${window.location.origin}/`,
      },
    });
  };

  return {
    isAuthenticated,
    isLoading,
    /** session.user — null when not authenticated */
    user: session.user,
    authMode: session.mode,
    loginWithRedirect,
    logout,
    /** Backward compat — returns server access token */
    getAccessTokenSilently: async () => getSession().accessToken,
    /** Backward compat — use global fetchWithAuth instead */
    fetchWithAuth: async (path: string, init: RequestInit = {}) => {
      const { fetchWithAuth: fwa } = await import("@/lib/fetchWithAuth");
      const res = await fwa(path, init);
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
      const text = await res.text();
      return text ? JSON.parse(text) : null;
    },
  };
}
